import React, { useCallback, forwardRef } from 'react';
import PropTypes from 'prop-types';

import { Tracking } from 'bdvp-tracking';

import Carousel from '../Carousel';
import MatchupCard from '../MatchupCard';
import RowHeading from '../RowHeading';

import { CarouselRowType } from '../../utils/constants';

import { PARTIAL_VISIBILITY_GUTTER, LAYOUT_ITEMS } from './constants';
import Card from './Card';

const carouselType = CarouselRowType.GAMES;

const GamesRow = forwardRef(({
    title,
    list,
    hasMore,
    fetchMoreData,
    isLoading,
    isLoadingMore,
    limit,
    rank,
    ...otherProps
}, ref) => {

    const onAfterChangeHandler = useCallback((isScrollLeft) => {

        Tracking.trackCarouselScroll({
            carouselRank: rank,
            carouselTitle: title,
            isScrollLeft,
            carouselType: carouselType,
        });

    }, [
        rank,
        title,
    ]);

    const onItemClickHandler = useCallback((event, gamePk, index) => {

        Tracking.trackCarouselOnItemClick({
            carouselRank: rank,
            carouselTitle: title,
            index,
            id: gamePk,
            carouselType: carouselType,
        });

    }, [
        rank,
        title,
    ]);

    const renderItem = useCallback((item, isLoading, index = 0) => {

        if (isLoading) {

            return <MatchupCard index={index} loading />;

        }

        const { home, away } = item.mediaPlayback?.[0]?.playInfo?.teams ?? {
            home: {},
            away: {},
        };

        return (
            <Card
                index={index}
                gamePk={item.gamePk}
                gameDate={item.gameDate}
                homeTeamId={home.id}
                awayTeamId={away.id}
                onClick={onItemClickHandler}
            />
        );

    }, [onItemClickHandler]);

    if (list.length === 0 && !isLoading) {

        return null;

    }

    return (
        <div ref={ref} {...otherProps}>
            <RowHeading
                loading={isLoading}
                text={title}
                rank={rank}
                enableLeftRightMargin
            />
            <Carousel
                hasMore={hasMore}
                isLoading={isLoading}
                isLoadingMore={isLoadingMore}
                limit={limit}
                list={list}
                fetchMoreData={fetchMoreData}
                renderItem={renderItem}
                onAfterChange={onAfterChangeHandler}
                partialVisibilityGutter={PARTIAL_VISIBILITY_GUTTER}
                layoutItems={LAYOUT_ITEMS}
            />
        </div>
    );

});

GamesRow.propTypes = {
    fetchMoreData: PropTypes.func.isRequired,
    isLoading: PropTypes.bool.isRequired,
    isLoadingMore: PropTypes.bool.isRequired,
    limit: PropTypes.number.isRequired,
    rank: PropTypes.number.isRequired,
    hasMore: PropTypes.bool.isRequired,
    title: PropTypes.string.isRequired,
    list: PropTypes.array.isRequired,
};

export default GamesRow;
